import styles from './style.module.css';

import {useEffect, useState, memo} from 'react';
import {createPortal} from 'react-dom';
import {XIcon} from '@phosphor-icons/react';

import {classes} from '@/util';
import {useScrollLock} from '@/hooks/useScrollLock';
import {useEscape} from '@/hooks/useEscape';

interface ModalProps {
    isOpen: boolean;
    title?: string;
    footerActions?: React.ReactNode;
    transparentBackground?: boolean;
    autoSize?: boolean;
    width?: string;
    height?: string;
    className?: string;
    padding?: string;
    onClose: (event?: React.MouseEvent | KeyboardEvent) => void;
    children: React.ReactNode;
}

export const Modal = memo(
    ({
        isOpen,
        title,
        footerActions,
        transparentBackground = false,
        autoSize = false,
        width,
        height,
        className = '',
        padding,
        onClose,
        children,
    }: ModalProps) => {
        const [mounted, setMounted] = useState(false);

        useEffect(() => {
            setMounted(true);
            return () => setMounted(false);
        }, []);

        useScrollLock();
        useEscape((event: KeyboardEvent) => {
            if (isOpen) onClose(event);
        });

        if (!isOpen) return null;

        const handleBackdropClick = (event: React.MouseEvent) => {
            if (event.target === event.currentTarget) {
                onClose(event);
            }
        };

        const containerStyle: React.CSSProperties = {};

        if (!autoSize) {
            containerStyle.width = width ?? '60rem';
            containerStyle.height = height ?? '37.5rem';
        } else {
            containerStyle.width = width ?? 'auto';
            containerStyle.height = height ?? 'auto';
        }

        if (padding) containerStyle.padding = padding;

        const modalJsx = (
            <div
                onClick={handleBackdropClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'modal-title' : undefined}
                className={classes(styles.backdrop, {
                    [styles.transparentBackdrop]: transparentBackground,
                })}
            >
                <div
                    className={classes(styles.modalContainer, className)}
                    style={containerStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {title && (
                        <h2 id="modal-title" className={styles.modalTitle}>
                            {title}
                        </h2>
                    )}
                    {onClose && (
                        <button
                            aria-label="Close modal"
                            className={styles.closeButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose(e);
                            }}
                        >
                            <XIcon size={24} />
                        </button>
                    )}
                    <div className={styles.modalContent}>{children}</div>
                    {footerActions && (
                        <div className={styles.modalFooter}>
                            {footerActions}
                        </div>
                    )}
                </div>
            </div>
        );

        return mounted ? createPortal(modalJsx, document.body) : null;
    }
);
